import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Globals } from './globals';
import { FormTemplate, FormTemplateType } from 'app/models/template';
import * as _ from "lodash";
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserService } from './user.service';
import { FormSubmission } from 'app/models/form-submission';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { ExternalOperationService } from './external-operation.service';
import { FormQueryExternalOperation, FormSubmitExternalOperation, FormSubmitExternalOperationParameter, FormSubmitExternalOperationParameterBindType } from 'app/models/form-external-operation';
import { ConnectedAppEndpoint } from 'app/models/connected-app';
import { initiateDownload } from 'app/helpers/utils';
import { PaginatedResponse } from 'app/models/paginated-response';


@Injectable({
  providedIn: 'root'
})
export class FormTemplateService {

constructor(private http: HttpClient, private globals: Globals, private userService: UserService,
            private formBuilder: FormBuilder, private externalOperationService: ExternalOperationService) { }

  list(path:string) {
    let options = {path:path};
    return this.http.post<FormTemplate[]>(`${this.globals.api_base_url}/form-templates/list` ,options);
  }

  listDeleted() {
    return this.http.get<FormTemplate[]>(`${this.globals.api_base_url}/form-templates/list_deleted`);  
  }

  getById(id: number) {
    return this.http.get<FormTemplate>(`${this.globals.api_base_url}/form-templates/form/${id}`).pipe(tap((f) => {
      if (f.form_type_id == FormTemplateType.PDF)
        f.schema.settings.pdf.src = `${this.globals.api_base_url}/form-templates/form/${f.id}/pdf_html?bearer=${this.userService.access_token}`;

    }));
  }

  createFolder(folder_name: string, path: string) {
    const obj = { "path": path, "folder_name": folder_name };
    return this.http.post(`${this.globals.api_base_url}/form-templates/create_folder`, obj);
  }

  deleteFolder(path: string) {
      const obj = { "path": path };
      return this.http.post(`${this.globals.api_base_url}/form-templates/delete_folder`, obj);
  }

  deleteForm(path: string) {
    const obj = { "path": path };
    return this.http.post(`${this.globals.api_base_url}/form-templates/delete`, obj);
  }

  restoreForm(path: string) {
    const obj = { "path": path };
    return this.http.post(`${this.globals.api_base_url}/form-templates/restore`, obj);
  }

  copyForm(srcPath: string, destPath: string, destIndexBasename: string) {
    const obj = { "src_path": srcPath, "dest_path": destPath, "index_basename": destIndexBasename };
    return this.http.post(`${this.globals.api_base_url}/form-templates/copy`, obj);
  }

  copyFolder(srcPath: string, destPath: string) {
      const obj = { "src_path": srcPath, "dest_path": destPath };
      return this.http.post(`${this.globals.api_base_url}/form-templates/copy_folder`, obj);
  }

  moveForm(srcPath: string, destPath: string) {
      const obj = { "src_path": srcPath, "dest_path": destPath };
      return this.http.post(`${this.globals.api_base_url}/form-templates/move`, obj);
  }

  moveFolder(srcPath: string, destPath: string) {
      const obj = { "src_path": srcPath, "dest_path": destPath };
      return this.http.post(`${this.globals.api_base_url}/form-templates/move_folder`, obj);
  }

  publish(id: number = null, path: string = null) {
    const obj = { id: id, path: path};
    return this.http.post(`${this.globals.api_base_url}/form-templates/publish`, obj);
  }

  putDraft(formTemplate: FormTemplate, pdfFile: File = null, storage_id: number = null, pdf_path: string = null) {
    const obj = _.merge(formTemplate, { file: pdfFile, storage_id: storage_id, pdf_path: pdf_path});
    obj.schema = JSON.stringify(formTemplate.schema);
    return this.http.put<FormTemplate>(`${this.globals.api_base_url}/form-templates/draft`, obj).pipe(tap((f) => {
      if (f.form_type_id == FormTemplateType.PDF)
        f.schema.settings.pdf.src = `${this.globals.api_base_url}/form-templates/form/${f.id}/pdf_html?bearer=${this.userService.access_token}`;

    }));
  }

  getOrCreateDraft(formTemplate: FormTemplate, pdfFile: File = null, storage_id: number = null, pdf_path: string = null, index_basename: string = null) {
    const obj = _.merge(formTemplate, { file: pdfFile, storage_id: storage_id, pdf_path: pdf_path});
    return this.http.post<FormTemplate>(`${this.globals.api_base_url}/form-templates/get_or_create_draft`, obj).pipe(tap((f) => {
      if (f.form_type_id == FormTemplateType.PDF)
        f.schema.settings.pdf.src = `${this.globals.api_base_url}/form-templates/form/${f.id}/pdf_html?bearer=${this.userService.access_token}`;

    }));
  }

  isDataIndexNameUnique(index_basename: string) {
    return this.http.get(`${this.globals.api_base_url}/form-templates/is_index_basename_unique`, {
      params: {
        index_basename: index_basename
      }
    });
  }

  hasDraft(formTemplate: FormTemplate) {
    return this.http.post(`${this.globals.api_base_url}/form-templates/has_draft`, formTemplate);
  }

  deleteDraft(formTemplate: FormTemplate) {
    return this.http.post(`${this.globals.api_base_url}/form-templates/delete_draft`, formTemplate);
  }

  versions(id: number) {
    return this.http.get<FormTemplate[]>(`${this.globals.api_base_url}/form-templates/form/${id}/versions`);
  }

  restoreVersion(id: number, version: number) {
    return this.http.post(`${this.globals.api_base_url}/form-templates/form/${id}/version/${version}/restore`, {});
  }

  submissions(id: number, pageNumber: number = 1, filters: {[key: string]: string|number} = {}) {
    return this.http.post<PaginatedResponse<FormSubmission>>(`${this.globals.api_base_url}/form-templates/form/${id}/submissions`, {
      filters: filters,
      page: pageNumber.toString()
    });
  }

  getSubmissionById(id: number) {
    return this.http.get<FormSubmission>(`${this.globals.api_base_url}/form-templates/submission/${id}`);       
  }

  generateSubmissionPDFById(id: number) {
    return this.http.post<any>(`${this.globals.api_base_url}/form-templates/submission/${id}/pdf`, {});       
  }

  downloadSubmissionPDFById(id: number) {
    initiateDownload(`${this.globals.api_base_url}/form-templates/submission/${id}/pdf?bearer=${this.userService.access_token}`);
  }

  deleteSubmissionById(id: number) {
    return this.http.delete(`${this.globals.api_base_url}/form-templates/submission/${id}`);       
  }

  putSubmission(formSubmission: FormSubmission) {
    formSubmission.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http.put<FormSubmission>(`${this.globals.api_base_url}/form-templates/submission`, formSubmission);
  }

  getRecents() {
    return this.http.get<FormTemplate[]>(`${this.globals.api_base_url}/form-templates/recents`);  
  }

  getFormQueryExternalOperations(formId: number) {
    return this.http.get<FormQueryExternalOperation[]>(`${this.globals.api_base_url}/form-templates/form/${formId}/query_external_operations`);
  }

  putFormQueryExternalOperations(formId: number, formQueryExternalOperation: FormQueryExternalOperation) {
    return this.http.put<FormQueryExternalOperation[]>(`${this.globals.api_base_url}/form-templates/form/${formId}/query_external_operation`, formQueryExternalOperation);
  }

  deleteFormQueryExternalOperation(formId: number, extOpId: number) {
    return this.http.delete<FormQueryExternalOperation[]>(`${this.globals.api_base_url}/form-templates/form/${formId}/query_external_operation/${extOpId}`);
  }

  getFormSubmitExternalOperations(formId: number) {
    return this.http.get<FormSubmitExternalOperation[]>(`${this.globals.api_base_url}/form-templates/form/${formId}/submit_external_operations`);
  }

  putFormSubmitExternalOperations(formId: number, formSubmitExternalOperation: FormSubmitExternalOperation) {
    return this.http.put<FormSubmitExternalOperation[]>(`${this.globals.api_base_url}/form-templates/form/${formId}/submit_external_operation`, formSubmitExternalOperation);
  }

  deleteFormSubmitExternalOperation(formId: number, extOpId: number) {
    return this.http.delete<FormSubmitExternalOperation[]>(`${this.globals.api_base_url}/form-templates/form/${formId}/submit_external_operation/${extOpId}`);
  }

  executeFormQueryExternalOperation(formId: number, uniqueName: string, options: any, externalAccessToken = undefined) {
    options = _.cloneDeep(options || {});
    options['unique_name'] = uniqueName;
    if (externalAccessToken) {
      options['external_access_token'] =  externalAccessToken;
      return this.http.post(`${this.globals.api_base_url}/external/form-templates/form/${formId}/query_external_operation/execute`, options);
    } else {
      return this.http.post(`${this.globals.api_base_url}/form-templates/form/${formId}/query_external_operation/execute`, options);
    }
  }
  
  dataIndexNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      return timer(500).pipe(
        switchMap(() => {
          return this.isDataIndexNameUnique(control.value).pipe(
            map(() => { return null; }),
            catchError(() => of({unique: true}))
          );
        })
      );
    }
  }
  lowercaseValidator(c: FormControl) {
    let regex = /[A-Z]/g
    if (regex.test(c.value)) {
      return { lowercase: true }
    } else {
      return null;
    }
  }
  illegalIndexBasenameStartValidator(c: FormControl) {
    let regex = /^[_\-\+\/]/;
    if (regex.test(c.value)) {
      return { dataIndexIllegalStart: true }
    } else {
      return null;
    }
  }
  illegalIndexBasenameCharsValidator(c: FormControl) {
    let regex = /[_<>:;,?"*|\/\\\s]/g;
    if (regex.test(c.value)) {
      return { dataIndexIllegalChars: true }
    } else {
      return null;
    }
  }

  illegalFormCharsValidator(c: FormControl) {
    let regex = /[<>?"*|\/\\]/g;
    if (regex.test(c.value)) {
      return { formNameIllegalChars: true }
    } else {
      return null;
    }
  }

  formQueryExternalOperationFormGroup(formQueryExternalOperation: FormQueryExternalOperation = null) {
    let frmGrp: FormGroup = this.formBuilder.group({
        id: [(formQueryExternalOperation)? formQueryExternalOperation.id: null],
        unique_name: [(formQueryExternalOperation)? formQueryExternalOperation.unique_name: null, Validators.required],
        external_operation_common_settings: this.externalOperationService.externalOperationCommonSettingsFormGroup((formQueryExternalOperation)? formQueryExternalOperation.external_operation_common_settings: null)
    });        
    return frmGrp;
  }

  formSubmitExternalOperationFormGroup(formSubmitExternalOperation: FormSubmitExternalOperation = null) {
    let frmGrp: FormGroup = this.formBuilder.group({
        id: [(formSubmitExternalOperation)? formSubmitExternalOperation.id: null],
        parameters: this.formSubmitExternalOperationParametersFormArray((formSubmitExternalOperation? (formSubmitExternalOperation.parameters || []): [])),
        external_operation_common_settings: this.externalOperationService.externalOperationCommonSettingsFormGroup((formSubmitExternalOperation)? formSubmitExternalOperation.external_operation_common_settings: null)
    });        
    return frmGrp;
  }

  updateSubmitExternalOperationParametersFromEndpoint(formGroup: FormGroup, endpoint: ConnectedAppEndpoint) {
      let newParameters: FormSubmitExternalOperationParameter[] = [];
      if (endpoint) {
          let newEndpoint: ConnectedAppEndpoint = <ConnectedAppEndpoint>endpoint;
          (newEndpoint.parameters || []).forEach((p) => {
              newParameters.push(<FormSubmitExternalOperationParameter>{
                  connected_app_parameter: p,
                  connected_app_parameter_id: p.id
              });
          });
      }
      let newParametersFormArray = this.formSubmitExternalOperationParametersFormArray(newParameters);
      formGroup.controls['parameters'] = newParametersFormArray;
      newParametersFormArray.setParent(formGroup);        
  }

  formSubmitExternalOperationParametersFormArray(parameters: FormSubmitExternalOperationParameter[]) {
      let parametersFormArray = this.formBuilder.array([]);
      parameters.forEach((p) => {
          parametersFormArray.push(this.formBuilder.group({
              id: [p.id],
              bind_type: [p.bind_type || ((p.connected_app_parameter.default_value)? FormSubmitExternalOperationParameterBindType.CONSTANT_VALUE: FormSubmitExternalOperationParameterBindType.FIELD_SUBMISSION_PATH), Validators.required],
              constant_value: [p.constant_value || p.connected_app_parameter.default_value, [(c: AbstractControl) => { return (c.parent && c.parent.get('bind_type').value == FormSubmitExternalOperationParameterBindType.CONSTANT_VALUE)? Validators.required(c): null}]],
              script: [p.script, [(c: AbstractControl) => { return (c.parent && c.parent.get('bind_type').value == FormSubmitExternalOperationParameterBindType.SCRIPT)? Validators.required(c): null}]],
              field_submission_path: [p.field_submission_path, [(c: AbstractControl) => { return (c.parent && c.parent.get('bind_type').value == FormSubmitExternalOperationParameterBindType.FIELD_SUBMISSION_PATH)? Validators.required(c): null}]],
              connected_app_parameter: [p.connected_app_parameter, Validators.required],
              connected_app_parameter_id: [p.connected_app_parameter_id, Validators.required]
          }));
      });

      return parametersFormArray;
  }

}
